<script setup lang="ts">
    import { Material } from "@/store/modules/materialModule";
    import cloneDeep from "lodash/cloneDeep";
    import { computed, onMounted, ref, toRefs, watch } from "vue";

    const emit = defineEmits<{
        (
            event: "input",
            payload: {
                [key: string]: string;
            }
        ): void;
    }>();

    const props = withDefaults(
        defineProps<{
            submit: (payload: Material | Event) => void;
            defaultValues?: unknown;
            columns?: "1" | "2" | "3" | "4";
            useFormulate?: boolean;
            errors?: {
                [key: string]: string[];
            };
        }>(),
        {
            columns: "4",
            useFormulate: false,
            defaultValues: () => ({}),
            errors: () => ({}),
        }
    );

    const { defaultValues, columns, errors, useFormulate, submit } = toRefs(props);
    const spanThree = computed(() => Number(columns?.value) + 1);
    const spanTwo = computed(() => Number(columns?.value));
    const formData = ref();

    const emitInput = (updates: { [key: string]: string }) => {
        console.log(updates);
        emit("input", updates);
    };

    onMounted(() => {
        if (defaultValues?.value) {
            formData.value = cloneDeep(defaultValues.value);
        }
    });

    watch(defaultValues, (newDefaultValues) => {
        formData.value = cloneDeep(newDefaultValues);
    });
</script>

<template>
    <FormulateForm v-if="useFormulate" v-model="formData" @submit="submit" @input="emitInput" :errors="errors">
        <slot name="header" />
        <div class="form">
            <slot name="form-content" />
        </div>
        <div class="footer">
            <slot name="footer" />
        </div>
    </FormulateForm>
    <form v-else @submit.prevent="submit">
        <slot name="header" />
        <div class="form">
            <slot name="form-content" />
        </div>
        <div class="footer">
            <slot name="footer" />
        </div>
    </form>
</template>

<style lang="scss" scoped>
    @import "@/style/form";
    @import "@/style/container";

    .form {
        width: 100%;
        display: grid;
        grid-template-columns: repeat(auto-fill, 16rem);
        grid-column-gap: $form-column-gap;

        ::v-deep .formulate-input {
            position: relative;
            margin: 0;

            .formulate-input-errors {
                font-size: 14px;
                line-height: 1rem;
                font-weight: 400;
                position: absolute;
                max-width: 100%;
            }
        }

        ::v-deep .form-container {
            display: grid;
            grid-template-columns: subgrid;
            grid-column: 1 / -1;
            grid-row-gap: $form-row-gap;
            grid-auto-flow: row;

            .form-grouping {
                display: grid;
                grid-template-columns: subgrid;
                grid-column: 1 / -1;
                grid-row-gap: $form-row-gap;

                &.three,
                .three {
                    grid-column: 1 / v-bind(spanThree);
                }

                &.two,
                .two {
                    grid-column: 1 / v-bind(spanTwo);
                }

                .button {
                    // Samma storlek som line-height på input label
                    align-self: flex-end;
                    width: max-content;
                    max-width: 100%;
                }
            }

            .input-wrapper {
                min-width: max-content;
                padding-bottom: 0;
                margin-bottom: 0;
                display: grid;
                align-items: center;

                .error:not(:first-of-type),
                .information,
                .warning:not(:first-of-type) {
                    top: 100%;
                }
            }
        }

        ::v-deep .collapsible-section {
            display: grid;
            grid-column: 1 / -1;
            grid-template-columns: subgrid;

            .header,
            .inner,
            .outer {
                grid-column: 1 / -1;
            }

            .outer,
            .inner {
                display: grid;
                grid-template-columns: subgrid;
            }
        }
    }

    .footer {
        margin-top: $form-row-gap;
    }
</style>
